import type { Role, ModelStr } from "byzantine/src/Role/Role";
import { MODEL_STR, ALL } from "byzantine/src/Role/Role";
import OrganizationUser from "byzantine/src/OrganizationUser";

export const DEFAULT_ROLE_NAMES = new Set(["Admin", "Viewer"]);

export type AccountAccessResult = {
  accounts: (API.Account | API.ExternalAccount)[];
  hasAccessToAllAccounts: boolean;
};

export type PermissionDescription = {
  titleFtlId: string;
  subtitleFtlId?: string;
  modelStr?: string;
};

type PermissionGroup = {
  titleFtlId: string;
  basePermission: ModelStr;
  templatePermissions: ModelStr[];
  subtitleFtlIds: {
    both: string;
    templateOnly: string;
  };
  appendSubtitleFtlId?: string;
  modifierPermissions?: ModelStr[];
};

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    titleFtlId: "org-role-perm-make-internal-transfers",
    basePermission: MODEL_STR.INTRA_INSTITUTION_TRANSFER,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-make-external-transfers",
    basePermission: MODEL_STR.ACH_TRANSFER,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-add-external-accounts",
    basePermission: MODEL_STR.EXTERNAL_ACCOUNT,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-send-ach-payments",
    basePermission: MODEL_STR.ACH_PAYMENT,
    templatePermissions: [
      MODEL_STR.ACH_PAYMENT_VIA_TEMPLATE,
    ],
    subtitleFtlIds: {
      both: "org-role-perm-send-ach-payments-subtitle",
      templateOnly: "org-role-perm-send-ach-payments-subtitle-template-only",
    },
    appendSubtitleFtlId: "org-role-perm-send-ach-payments-modifier",
    modifierPermissions: [MODEL_STR.UNVERIFIED_FEDNOW_SEND],
  },
  {
    titleFtlId: "org-role-perm-collect-ach-payments",
    basePermission: MODEL_STR.COLLECT_ACH_PAYMENT,
    templatePermissions: [
      MODEL_STR.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
    ],
    subtitleFtlIds: {
      both: "org-role-perm-collect-ach-payments-subtitle",
      templateOnly: "org-role-perm-collect-ach-payments-subtitle-template-only",
    },
  },
  {
    titleFtlId: "org-role-perm-send-ach-passthrough",
    basePermission: MODEL_STR.ACH_PASSTHROUGH_SEND,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-collect-ach-passthrough",
    basePermission: MODEL_STR.ACH_PASSTHROUGH_COLLECT,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-manage-ach-templates",
    basePermission: MODEL_STR.BULK_PAYMENT_TEMPLATE,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "org-role-perm-manage-ach-templates-subtitle",
      templateOnly: "org-role-perm-manage-ach-templates-subtitle",
    },
  },
  {
    titleFtlId: "org-role-perm-approve-ach-templates",
    basePermission: MODEL_STR.APPROVE_ACH_TEMPLATES,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-send-intl-wires",
    basePermission: MODEL_STR.INTERNATIONAL_WIRE,
    templatePermissions: [MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES],
    subtitleFtlIds: {
      both: "org-role-perm-send-intl-wires-subtitle",
      templateOnly: "org-role-perm-send-intl-wires-subtitle-template-only",
    },
  },
  {
    titleFtlId: "org-role-perm-manage-intl-wire-templates",
    basePermission: MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "org-role-perm-manage-intl-wire-templates-subtitle",
      templateOnly: "org-role-perm-manage-intl-wire-templates-subtitle",
    },
  },
  {
    titleFtlId: "org-role-perm-send-domestic-wires",
    basePermission: MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
    templatePermissions: [
      MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
    ],
    subtitleFtlIds: {
      both: "org-role-perm-send-domestic-wires-subtitle",
      templateOnly: "org-role-perm-send-domestic-wires-subtitle-template-only",
    },
  },
  {
    titleFtlId: "org-role-perm-manage-domestic-wire-templates",
    basePermission: MODEL_STR.DOMESTIC_WIRE_TEMPLATES,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "org-role-perm-manage-domestic-wire-templates-subtitle",
      templateOnly: "org-role-perm-manage-domestic-wire-templates-subtitle",
    },
  },
  {
    titleFtlId: "org-role-perm-access-business-zelle",
    basePermission: MODEL_STR.ZELLE,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-manage-payment-recipients",
    basePermission: MODEL_STR.RECIPIENT,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-pay-bills",
    basePermission: MODEL_STR.PAYMENT,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-mobile-check-deposits",
    basePermission: MODEL_STR.RDC,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-approve-dual-approvals",
    basePermission: MODEL_STR.DUAL_APPROVAL_REQUEST,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
  {
    titleFtlId: "org-role-perm-create-api-tokens",
    basePermission: MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES,
    templatePermissions: [],
    subtitleFtlIds: {
      both: "",
      templateOnly: "",
    },
  },
];

const PERMISSION_DISPLAY_ORDER: ModelStr[] = [
  MODEL_STR.INTRA_INSTITUTION_TRANSFER,
  MODEL_STR.ACH_TRANSFER,
  MODEL_STR.EXTERNAL_ACCOUNT,
  MODEL_STR.ACH_PAYMENT,
  MODEL_STR.COLLECT_ACH_PAYMENT,
  MODEL_STR.ACH_PASSTHROUGH_SEND,
  MODEL_STR.ACH_PASSTHROUGH_COLLECT,
  MODEL_STR.BULK_PAYMENT_TEMPLATE,
  MODEL_STR.APPROVE_ACH_TEMPLATES,
  MODEL_STR.INTERNATIONAL_WIRE,
  MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES,
  MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
  MODEL_STR.DOMESTIC_WIRE_TEMPLATES,
  MODEL_STR.ZELLE,
  MODEL_STR.RECIPIENT,
  MODEL_STR.PAYMENT,
  MODEL_STR.RDC,
  MODEL_STR.DUAL_APPROVAL_REQUEST,
  MODEL_STR.BANKING_API_TOKEN_ALLOWED_SCOPES,
];

export function getPermissionDescriptions(
  permissions: API.Permission[]
): PermissionDescription[] {
  const hasGlobalWildcard = permissions.some(
    (permission) =>
      permission.model_str === "*" &&
      permission.operation === "*" &&
      permission.uuid === "*"
  );

  // If global wildcard exists, return all permissions in order with subtitles where applicable
  if (hasGlobalWildcard) {
    return PERMISSION_DISPLAY_ORDER.map((basePermission) => {
      const group = PERMISSION_GROUPS.find(
        (g) => g.basePermission === basePermission
      );
      let titleFtlId: string = "";
      let subtitleFtlId: string | undefined;

      if (group) {
        titleFtlId = group.titleFtlId;
        // For global wildcard, use the "both" subtitle which represents full access
        subtitleFtlId = group.subtitleFtlIds.both;

        if (group.modifierPermissions && group.appendSubtitleFtlId) {
          subtitleFtlId = `${subtitleFtlId}|${group.appendSubtitleFtlId}`;
        }
      }

      return {
        titleFtlId,
        subtitleFtlId,
        modelStr: basePermission,
      };
    });
  }

  const seenTitleFtlIds = new Set<string>();
  const resultMap = new Map<string, PermissionDescription>();

  const writePermissionModelStrs = new Set<string>();
  permissions.forEach((permission) => {
    if (permission.operation === "write" || permission.operation === ALL) {
      writePermissionModelStrs.add(permission.model_str);
    }
  });

  PERMISSION_GROUPS.forEach((group) => {
    const hasBasePermission = writePermissionModelStrs.has(
      group.basePermission
    );
    const hasTemplatePermission = group.templatePermissions.some((modelStr) =>
      writePermissionModelStrs.has(modelStr)
    );

    if (hasBasePermission || hasTemplatePermission) {
      if (!seenTitleFtlIds.has(group.titleFtlId)) {
        seenTitleFtlIds.add(group.titleFtlId);

        let subtitleFtlId: string | undefined;
        if (hasBasePermission && hasTemplatePermission) {
          subtitleFtlId = group.subtitleFtlIds.both;
        } else if (hasTemplatePermission) {
          subtitleFtlId = group.subtitleFtlIds.templateOnly;
        }
        // If only base permission is present, no subtitle

        // Check for modifier permissions to append to subtitle
        if (group.modifierPermissions && group.appendSubtitleFtlId) {
          const hasModifierPermission = group.modifierPermissions.some(
            (modelStr) => writePermissionModelStrs.has(modelStr)
          );
          if (hasModifierPermission) {
            subtitleFtlId = subtitleFtlId
              ? `${subtitleFtlId}|${group.appendSubtitleFtlId}`
              : group.appendSubtitleFtlId;
          }
        }

        resultMap.set(group.titleFtlId, {
          titleFtlId: group.titleFtlId,
          subtitleFtlId,
          modelStr: group.basePermission,
        });
      }
    }
  });

  const sortedPermissions = Array.from(resultMap.values()).sort((a, b) => {
    const indexA = PERMISSION_DISPLAY_ORDER.indexOf(a.modelStr || "");
    const indexB = PERMISSION_DISPLAY_ORDER.indexOf(b.modelStr || "");

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return 0;
  });

  return sortedPermissions;
}

export function translatePermissionFtlIds(
  permission: PermissionDescription,
  l10n: { getString: (id: string) => string }
): { translatedTitle: string; translatedSubtitle?: string } {
  const translatedTitle = l10n.getString(permission.titleFtlId);

  let translatedSubtitle: string | undefined;
  if (permission.subtitleFtlId) {
    if (
      permission.subtitleFtlId.includes("external account") ||
      permission.subtitleFtlId.includes("external accounts")
    ) {
      translatedSubtitle = permission.subtitleFtlId;
    } else if (permission.subtitleFtlId.includes("|")) {
      const parts = permission.subtitleFtlId.split("|");
      const translatedParts = parts.map((ftlId) => l10n.getString(ftlId));
      translatedSubtitle = translatedParts.join(" ");
    } else {
      translatedSubtitle = l10n.getString(permission.subtitleFtlId);
    }
  }

  return { translatedTitle, translatedSubtitle };
}

export function splitRoles(roles: Role[]) {
  const defaultRoles: Role[] = [];
  const customRoles: Role[] = [];

  roles.forEach((role) => {
    if (DEFAULT_ROLE_NAMES.has(role.name)) {
      defaultRoles.push(role);
    } else {
      customRoles.push(role);
    }
  });

  // Sort default roles with Admin first
  defaultRoles.sort((a) => (a.name === "Admin" ? -1 : 1));

  return { defaultRoles, customRoles };
}

export const filterRolesByName = (roles: Role[], query: string): Role[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery === "") return roles;
  return roles.filter((role) =>
    role.name.toLowerCase().includes(normalizedQuery)
  );
};


export const getUsersForRole = (
  roleUuid: string | undefined,
  organizationUsers?: OrganizationUser[]
) => {
  if (!roleUuid) return [];
  return (organizationUsers || []).filter((user) => user.role?.uuid === roleUuid);
};

export const getActiveUsersForRole = (
  roleUuid: string | undefined,
  organizationUsers?: OrganizationUser[]
) => {
  if (!roleUuid) return [];
  return (organizationUsers || []).filter((user) => user.role?.uuid === roleUuid && user.status === OrganizationUser.STATUS.ACTIVE);
};

export const getActiveUserCountForRole = (
  roleUuid: string | undefined,
  organizationUsers?: OrganizationUser[]
) => {
  if (!roleUuid) return 0;
  return getActiveUsersForRole(roleUuid, organizationUsers).length;
};

export const getAccountAccessForRole = (
  role: Role | undefined,
  allAccounts: (API.Account | API.ExternalAccount)[] = []
): AccountAccessResult => {
  // Return early if no role
  if (!role || !role.permissions || role.permissions.length === 0) {
    return { accounts: [], hasAccessToAllAccounts: false };
  }

  // Check for global wildcard access (only model_str="*" or model_str="*" && operation="*")
  const hasGlobalWildcard = role.permissions.some(
    (permission) =>
      (permission.model_str === ALL && permission.operation === ALL) ||
      permission.model_str === ALL
  );

  if (hasGlobalWildcard) {
    return { accounts: allAccounts, hasAccessToAllAccounts: true };
  }

  // Check if user has wildcard access to both Account and ExternalAccount
  const hasAccountWildcard = role.permissions.some(
    (permission) =>
      permission.model_str === MODEL_STR.ACCOUNT && permission.uuid === ALL
  );
  const hasExternalAccountWildcard = role.permissions.some(
    (permission) =>
      permission.model_str === MODEL_STR.EXTERNAL_ACCOUNT &&
      permission.uuid === ALL
  );

  if (hasAccountWildcard && hasExternalAccountWildcard) {
    return { accounts: allAccounts, hasAccessToAllAccounts: true };
  }

  // Restricted access: extract specific account UUIDs from permissions
  const allowedAccountIds = new Set<string>();

  role.permissions.forEach((permission) => {
    if (
      (permission.model_str === MODEL_STR.ACCOUNT ||
        permission.model_str === MODEL_STR.EXTERNAL_ACCOUNT) &&
      permission.uuid !== ALL
    ) {
      allowedAccountIds.add(permission.uuid);
    }
  });

  // If they have wildcard for one type, add all of that type
  if (hasAccountWildcard) {
    allAccounts.forEach((account) => {
      if (account.source === "institution") {
        allowedAccountIds.add(String(account.id));
      }
    });
  }

  if (hasExternalAccountWildcard) {
    allAccounts.forEach((account) => {
      if (account.source === "external") {
        allowedAccountIds.add(String(account.id));
      }
    });
  }

  // Filter allAccounts to include only those with allowed IDs
  const filteredAccounts = allAccounts.filter((account) =>
    allowedAccountIds.has(String(account.id))
  );

  return { accounts: filteredAccounts, hasAccessToAllAccounts: false };
};

/**
 * Generate a dynamic subtitle for "Make external transfers" permission.
 * Shows the count and names of external accounts the role has access to.
 * Format: "4 external accounts: Navy Federal-0099, Checking-Eastern Shore, +2"
 *
 * @param role - The role to check external account access for
 * @param allAccounts - All available accounts (both institutional and external)
 * @returns Formatted subtitle string, or undefined if no external account access
 */
export function getExternalAccountSubtitle(
  role: Role | undefined,
  allAccounts: (API.Account | API.ExternalAccount)[] = []
): string | undefined {
  if (!role || !role.permissions || allAccounts.length === 0) {
    return undefined;
  }

  // Get allowed external accounts using the existing account access logic
  const { accounts: allowedAccounts } = getAccountAccessForRole(
    role,
    allAccounts
  );

  // Filter to only external accounts
  const externalAccounts = allowedAccounts.filter(
    (account) => account.source === "external"
  );

  if (externalAccounts.length === 0) {
    return undefined;
  }

  const total = externalAccounts.length;
  const accountLabel = total === 1 ? "external account" : "external accounts";

  // Show up to 2 full names, then "+X more" for the rest
  if (total <= 2) {
    const accountNames = externalAccounts.map((acc) => acc.name).join(", ");
    return `${total} ${accountLabel}: ${accountNames}`;
  }

  const firstTwo = externalAccounts.slice(0, 2).map((acc) => acc.name);
  const remainder = total - 2;
  return `${total} ${accountLabel}: ${firstTwo.join(", ")}, +${remainder}`;
}

// Helper to check if permissions include a global wildcard
const hasGlobalWildcard = (permissions: API.Permission[]): boolean =>
  permissions.some(
    (permission) =>
      permission.model_str === "*" &&
      permission.uuid === "*" &&
      ["write", "*"].includes(permission.operation)
  );

// Type definition for limit type data
export type LimitTypeData = {
  modelStrs: string[];
  ftlId: string;
};

export type LimitsData = {
  [key: string]: LimitTypeData;
};

// Limit types mapping with their related model strings and FTL IDs for display copy
export const LIMITS_DATA: LimitsData = {
  unverified_ach_push: {
    modelStrs: [MODEL_STR.ACH_PAYMENT],
    ftlId: "org-role-limit-type-unverified-ach-push",
  },
  unverified_ach_pull: {
    modelStrs: [MODEL_STR.ACH_PAYMENT_COLLECT],
    ftlId: "org-role-limit-type-unverified-ach-pull",
  },
  wire: {
    modelStrs: [
      MODEL_STR.DOMESTIC_WIRE_TRANSACTION,
      MODEL_STR.DOMESTIC_WIRE_TEMPLATES,
      MODEL_STR.DOMESTIC_WIRE_VIA_TEMPLATES,
    ],
    ftlId: "org-role-limit-type-wire",
  },
  international_wire: {
    modelStrs: [
      MODEL_STR.INTERNATIONAL_WIRE,
      MODEL_STR.INTERNATIONAL_WIRE_TEMPLATES,
      MODEL_STR.INTERNATIONAL_WIRE_VIA_TEMPLATES,
    ],
    ftlId: "org-role-limit-type-international-wire",
  },
  ach_push: {
    modelStrs: [MODEL_STR.ACH_TRANSFER],
    ftlId: "org-role-limit-type-ach-push",
  },
  fednow_unverified: {
    modelStrs: [MODEL_STR.UNVERIFIED_FEDNOW_SEND],
    ftlId: "org-role-limit-type-fednow-unverified",
  },
  ach_passthrough_send: {
    modelStrs: [MODEL_STR.ACH_PASSTHROUGH_SEND],
    ftlId: "org-role-limit-type-ach-passthrough-send",
  },
  ach_passthrough_collect: {
    modelStrs: [MODEL_STR.ACH_PASSTHROUGH_COLLECT],
    ftlId: "org-role-limit-type-ach-passthrough-collect",
  },
};

// Helper to check if a role has write permission for any of the provided modelStrs
export const hasWritePermissionForModels = (
  permissions: API.Permission[],
  modelStrs: string[]
): boolean => (
    hasGlobalWildcard(permissions) ||
    permissions.some(
      (p) =>
        modelStrs.includes(p.model_str) && ["write", "*"].includes(p.operation)
    )
  );

// Helper to check if a role has write permission for at least one limit type
export const hasAnyWritePermissionForLimits = (
  permissions: API.Permission[],
  limitsData: LimitsData
): boolean => (
    hasGlobalWildcard(permissions) ||
    Object.values(limitsData).some((data) =>
      hasWritePermissionForModels(permissions, data.modelStrs)
    )
  );
